import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cake, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { DataControls } from "@/components/DataControls";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [consent, setConsent] = useState(false);
  const [emailChart, setEmailChart] = useState("uk_singles");
  const [saving, setSaving] = useState(false);
  const [digest, setDigest] = useState(true);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      const u = r.data;
      setName(u.name || "");
      setDob(u.dob || "");
      setOptIn(!!u.birthday_emails_opt_in);
      setDigest(u.digest_emails_opt_in !== false);
      setConsent(!!u.gdpr_consent);
      setEmailChart(u.email_chart || "uk_singles");
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (optIn && !consent) {
      toast.error("Please tick GDPR consent to enable birthday emails");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/profile", {
        name,
        dob,
        birthday_emails_opt_in: optIn,
        digest_emails_opt_in: digest,
        gdpr_consent: consent,
        email_chart: emailChart,
      });
      await refreshUser();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-ink focus:outline-none focus:border-gold focus:bg-white transition-colors";
  const lbl = "block text-sm font-semibold text-ink mb-1.5";

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-3">Your account</p>
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-ink">Profile & preferences</h1>

        <form onSubmit={save} className="mt-8 space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
            <div>
              <label className={lbl}>Name</label>
              <input data-testid="account-name" value={name} onChange={(e) => setName(e.target.value)} className={field} />
            </div>
            <div>
              <label className={lbl}>Email</label>
              <input value={user?.email || ""} disabled className={field + " opacity-60 cursor-not-allowed"} />
            </div>
          </div>

          <div className="rounded-2xl border border-gold bg-paper p-6">
            <div className="flex items-center gap-2 mb-4">
              <Cake className="w-5 h-5 text-gold" />
              <h2 className="font-display text-lg font-bold text-ink">Birthday surprises</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className={lbl}>Date of birth</label>
                <input data-testid="account-dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} className={field} />
                <p className="mt-1 text-xs text-ink-muted">Used to send you a birthday horoscope and events from your birth year.</p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input data-testid="account-optin" type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} className="mt-1 w-4 h-4 accent-gold" />
                <span className="text-sm text-ink">Email me a birthday surprise each year (you can turn this off anytime).</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input data-testid="account-digest" type="checkbox" checked={digest} onChange={(e) => setDigest(e.target.checked)} className="mt-1 w-4 h-4 accent-gold" />
                <span className="text-sm text-ink">Weekly digest of my saved events + one new birthday twin (Sundays). Turning this off keeps your daily 7am and birthday emails.</span>
              </label>
              <div id="email-chart" className="pt-1">
                <label htmlFor="account-email-chart" className={lbl}>No.1 chart in my daily email</label>
                <select id="account-email-chart" data-testid="account-email-chart" value={emailChart} onChange={(e) => setEmailChart(e.target.value)} className={field}>
                  <option value="uk_singles">UK Singles (Official Charts)</option>
                  <option value="uk_albums">UK Albums (Official Charts)</option>
                  <option value="us_singles">US Singles (Billboard Hot 100)</option>
                  <option value="us_albums">US Albums (Billboard 200)</option>
                </select>
                <p className="mt-1.5 text-xs text-ink-muted">The 7am email shows the No.1 on this date 10–70 years ago from the chart you pick.</p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input data-testid="account-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-4 h-4 accent-gold" />
                <span className="text-sm text-ink flex items-start gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                  I consent to OddlyTrue storing my date of birth and sending me emails, per the privacy policy (GDPR). Consent is recorded with a timestamp.
                </span>
              </label>
            </div>
          </div>

          <button data-testid="account-save" type="submit" disabled={saving} className="w-full py-3 rounded-xl bg-ink text-white font-semibold hover:bg-gold hover:text-ink disabled:opacity-60 transition-colors">
            {saving ? "Saving..." : "Save changes"}
          </button>
        </form>
        <DataControls />
      </section>
      <Footer />
    </div>
  );
}
